import { Component, ReactNode, TransitionEvent as ReactTransitionEvent } from 'react'
import * as cn from 'classnames'
import * as groupBy from 'lodash/groupBy'
import * as throttle from 'lodash/throttle'
import * as styles from 'components/features/pfm/NotificationTray/NotificationTray.css'
import Animate from 'rc-animate'
import { defaultMemoize } from 'reselect'
import { Notification, NotificationShowMode } from 'interfaces/notifications'
import { InsightWithNotificatonsPlacement } from 'interfaces/insights'
import { InsightNotificationItem } from 'components/features/pfm/NotificationTray/InsightNotificationItem'
import { TrackerNotificationItem } from 'components/features/pfm/NotificationTray/TrackerNotificationItem'
import { LiveUpdateNotificationItem } from 'components/features/pfm/NotificationTray/LiveUpdateNotificationItem'
import Scrollable from 'components/atoms/Scrollable'
import { IS_MOBILE, IS_EDGE } from 'helpers/userAgentHelpers'
import { convertVhToPx } from 'helpers/styleHelpers'
import { OuterEventTrackerContext, OuterEventTrackerContextType } from 'components/utils/OuterEventTracker'
import withContext from 'hocs/withContext/withContext'
import { NotificationTrayRenderLevel } from 'interfaces/component'
import {
  NotificationTrayContextType,
  NotificationTrayContext,
  NotificationButtonContextProps,
} from 'components/features/pfm/NotificationTray/NotificationTrayContextProvider'
import { TextIconNotificationItem } from 'components/features/pfm/NotificationTray/TextIconNotificationItem'
import { PAGES_WITH_LIVE_UPDATE } from 'constants/pages'

type AnimationCallback = () => void

type ItemAnimationValue = {
  className: string
  height: number
}

type NotificationsByShowModeMap = Record<NotificationShowMode, Notification[]>

type NotificationTrayMode = 'sidebar' | 'settings'

export type NotificationTrayStateProps = {
  notifications: Notification[]
  isExpanded: boolean
  isMobileOrTablet: boolean
}

export type NotificationTrayDispatchProps = {
  toggleExpanded: (isExpanded: boolean) => void
  expandButtonShown: () => void
  notificationsShown: () => void
}

export type NotificationTrayOwnProps = {
  placement?: InsightWithNotificatonsPlacement
  theme?: NotificationTrayMode
  isPositionRelative?: boolean
  renderLevel: NotificationTrayRenderLevel
}

type NotificationTrayContextProps = {
  outerEventTracker: OuterEventTrackerContextType
  trayContext: NotificationTrayContextType
}

type NotificationTrayProps =
  & NotificationTrayStateProps
  & NotificationTrayDispatchProps
  & NotificationTrayOwnProps
  & NotificationTrayContextProps

/*
  Sometimes it's required to emulate closing of tray programmatically.
  It means tray should perform some actions depends on autoClosingStep
  (for example, start or cancel animations in proper moments).
*/
type Step =
  | 'waitingForUserAction'
  | 'trayWasExpanded'
  | 'trayWasAnimated'

const DEFAULT_TRAY_OFFSET = -parseInt(styles.notificationsBorderSize, 10)

const getNotificationsByShowMode = defaultMemoize((notifications: Notification[]): NotificationsByShowModeMap => {
  return {
    regular: [],
    fresh: [],
    pinned: [],
    ...groupBy(notifications, ({ showMode }) => showMode),
  }
})

class NotificationTray extends Component<NotificationTrayProps> {
  private rootNode: HTMLElement | null = null
  private regularSectionNode: HTMLElement | null = null
  private pinnedSectionNode: HTMLElement | null = null
  private scrollableNode: HTMLElement | null = null
  private scrollableComponent: Scrollable | null = null
  private trayAnimationFrame = 0
  private itemAnimationFrames: Set<number> = new Set()
  private trayAnimationCallbacks: Set<AnimationCallback> = new Set()
  private isExpandButtonShown = false
  private autoClosingStep: Step = 'waitingForUserAction'

  render () {
    const {
      notifications,
      isExpanded,
      theme,
      isPositionRelative,
    } = this.props

    const { regular, fresh, pinned } = getNotificationsByShowMode(notifications)

    const hasRegular = regular.length > 0
    const hasFresh = fresh.length > 0
    const hasPinned = pinned.length > 0

    const pageUsesLiveUpdate = PAGES_WITH_LIVE_UPDATE.includes(window.location.pathname)
    const onlyNotificationIsLiveUpdate = !!notifications.find(({ id }) => id === 'live-update')
    const shouldHideTray = onlyNotificationIsLiveUpdate && !pageUsesLiveUpdate
    const isVisible = !shouldHideTray && (hasRegular || hasPinned || hasFresh)

    const className = cn(styles.notificationTray, {
      [styles.sidebarMode]: !theme || theme === 'sidebar',
      [styles.settingsMode]: theme === 'settings',
      [styles.positionRelative]: !!isPositionRelative,
    })
    const innerContainerClassName = cn(styles.notificationTrayInner, {
      [styles.visible]: isVisible || isExpanded,
    })

    const regularSectionAnimation = {
      appear: this.handleExpandAnimationStart,
      enter: this.handleExpandAnimationStart,
      leave: this.handleCollapseAnimationStart,
    }

    return (
      <div
        className={ className }
        ref={ this.handleRootNodeRef }
        onTransitionEnd={ this.handleTrayTransitionEnd }
      >
        <div className={ innerContainerClassName }>
          <Scrollable
            ref={ this.handleScrollableComponentRef }
            contentClassName={ styles.scrollable }
            contentWrapperRef={ this.handleScrollableNodeRef }
          >
            <Animate animation={ regularSectionAnimation }>
              { isExpanded && (
                <section ref={ this.handleRegularSectionNodeRef }>
                  { this.renderItemList(regular) }
                </section>
              ) }
            </Animate>
            <section ref={ this.handlePinnedSectionNodeRef }>
              { this.renderItemList(fresh) }
              { this.renderItemList(pinned) }
            </section>
          </Scrollable>
        </div>
      </div>
    )
  }

  renderItemList (notifications: Notification[]) {
    const itemChildren = notifications.map((notification) => this.renderItem(notification))
    const listAnimation = {
      enter: this.handleItemEnterAnimationStart,
      leave: this.handleItemLeaveAnimationStart,
    }

    return (
      <ul className={ styles.itemList }>
        <Animate animation={ listAnimation }>
          { itemChildren }
        </Animate>
      </ul>
    )
  }

  renderItem (notification: Notification) {
    const { id } = notification
    const notificationChild: ReactNode = this.renderNotification(notification)

    if (!notificationChild) {
      return null
    }

    return (
      <li
        key={ id }
        data-notification-id={ id }
        className={ styles.item }
      >
        { notificationChild }
      </li>
    )
  }

  renderNotification (notification: Notification) {
    const pageUsesLiveUpdate = PAGES_WITH_LIVE_UPDATE.includes(window.location.pathname)
    switch (notification.type) {
      case 'insight':
        return (
          <InsightNotificationItem notification={ notification } isAnimating={ false } />
        )
      case 'tracker':
        return (
          <TrackerNotificationItem notification={ notification } />
        )
      case 'live-update':
        return pageUsesLiveUpdate && (
          <LiveUpdateNotificationItem notification={ notification } />
        )
      case 'text-icon':
        return (
          <TextIconNotificationItem notification={ notification } />
        )
      default:
        return null
    }
  }

  componentDidMount () {
    const { trayContext } = this.props
    trayContext.onNextButtonProps(this.getTrayButtonProps())
  }

  componentDidUpdate (prevProps: NotificationTrayProps) {
    const { trayContext, isExpanded, expandButtonShown, notifications } = this.props
    trayContext.onNextButtonProps(this.getTrayButtonProps())

    // Close tray automatically one frame later after it was opened
    if (this.autoClosingStep === 'trayWasAnimated') {
      this.autoClosingStep = 'waitingForUserAction'
      cancelAnimationFrame(this.trayAnimationFrame)
      this.trayAnimationFrame = requestAnimationFrame(this.close)
    }

    // Scroll to bottom if new notifications came
    if (notifications.length > prevProps.notifications.length) {
      this.scrollToBottom()
    }

    // Special animation for first single pinned or fresh notification (only tray is animated)
    const isSingleNotificationAppearing = !prevProps.notifications.length && notifications.length
    if (isSingleNotificationAppearing && this.pinnedSectionNode && this.isSpecialCaseForSingleNotification()) {
      this.handleTrayAnimationStart(() => {}, -this.pinnedSectionNode.scrollHeight, 0)
    }

    // Add or remove global event listeners depending on tray is expanded or not
    const isExpandedChanged = prevProps.isExpanded !== isExpanded
    if (isExpandedChanged) {
      if (isExpanded) {
        this.addEventListeners()
      } else {
        this.removeEventListeners()
      }
      if (IS_EDGE) {
        if (isExpanded) {
          this.handleExpandAnimationStart(null, this.forceUpdate)
        } else {
          this.handleCollapseAnimationStart(null, this.forceUpdate)
        }
      }
    }

    // Inform parent that expand button was shown (only once per lifecycle)
    if (!this.isExpandButtonShown && this.isExpandButtonVisible()) {
      this.isExpandButtonShown = true
      expandButtonShown()
    }
  }

  componentWillUnmount () {
    cancelAnimationFrame(this.trayAnimationFrame)
    this.itemAnimationFrames.forEach(cancelAnimationFrame)
    this.removeEventListeners()
  }

  private close = () => {
    this.props.toggleExpanded(false)
  }

  private open = () => {
    this.props.toggleExpanded(true)
  }

  private isExpandButtonVisible (): boolean {
    const { notifications } = this.props
    const { regular, fresh } = getNotificationsByShowMode(notifications)
    return regular.length !== 0 || fresh.length !== 0
  }

  private getNotificationByNode (node: HTMLElement): Notification | undefined {
    const notificationId = node.getAttribute('data-notification-id')
    const notification = this.props.notifications.find(({ id }) => id === notificationId)
    return notification
  }

  private isSpecialCaseForSingleNotification (): boolean {
    // Special animation for first single pinned or fresh notification
    if (!this.isExpandButtonShown && this.pinnedSectionNode) {
      const { notifications } = this.props
      if (notifications.length === 1 && notifications[0].showMode !== 'regular') {
        return true
      }
    }
    return false
  }

  private isItemAnimated (notification: Notification | undefined, isEnterAnimation: boolean): boolean {
    if (!notification) {
      // Any removed notification should be animated
      return true
    }

    // Appearing of first single pinned or fresh notification is not animated (only tray is animated)
    if (isEnterAnimation && this.isSpecialCaseForSingleNotification()) {
      return false
    }

    const { prevShowMode, showMode } = notification
    const isChangedFromFresh = prevShowMode === 'fresh' && showMode !== 'fresh'
    const isChangedToPinned = prevShowMode === 'fresh' || (prevShowMode === 'regular' && this.props.isExpanded) && showMode === 'pinned'

    return !isChangedFromFresh && !isChangedToPinned
  }

  private calculateTrayAnimationValues (): [number, number] {
    if (!this.scrollableNode || !this.pinnedSectionNode) {
      return [DEFAULT_TRAY_OFFSET, DEFAULT_TRAY_OFFSET]
    }

    const scrollableNodeHeight = parseInt(getComputedStyle(this.scrollableNode).height || '0px', 10)
    const pinnedSectionNodeHeight = this.pinnedSectionNode.getBoundingClientRect().height
    const offset = scrollableNodeHeight - pinnedSectionNodeHeight
    return [-offset, DEFAULT_TRAY_OFFSET]
  }

  private isOuterClickEvent = (target: HTMLElement): boolean => {
    const { buttonRootRef } = this.props.trayContext
    const buttonRootNode = buttonRootRef && buttonRootRef.current || null
    return !(
      buttonRootNode === target || buttonRootNode && buttonRootNode.contains(target)
      || this.rootNode === target || this.rootNode && this.rootNode.contains(target)
    )
  }

  private addEventListeners () {
    const { outerEventTracker } = this.props
    outerEventTracker.addOuterEventListener('tap click', this.close, this.isOuterClickEvent)
    outerEventTracker.addOuterEventListener('keydown', this.handleOuterKeyDown, this.rootNode)
    if (IS_MOBILE) {
      this.updateScrollableNodeMaxHeight()
      window.addEventListener('resize', this.updateScrollableNodeMaxHeightThrottled)
    }
  }

  private removeEventListeners () {
    const { outerEventTracker } = this.props
    outerEventTracker.removeOuterEventListener('tap click', this.close)
    outerEventTracker.removeOuterEventListener('keydown', this.handleOuterKeyDown)
    if (IS_MOBILE) {
      window.removeEventListener('resize', this.updateScrollableNodeMaxHeightThrottled)
      this.updateScrollableNodeMaxHeightThrottled.cancel()
    }
  }

  private scrollToBottom () {
    const { scrollableComponent, regularSectionNode } = this
    const INFINITY = 99999
    if (scrollableComponent) {
      scrollableComponent.updateScrollPosition(INFINITY)
    }
    if (regularSectionNode) {
      regularSectionNode.scrollTo(0, INFINITY)
    }
  }

  private updateScrollableNodeMaxHeight = () => {
    if (!this.scrollableNode) {
      return
    }
    this.scrollableNode.style.maxHeight = convertVhToPx(styles.notificationsMaxHeightMobile)
  }


  private getTrayButtonProps = (): NotificationButtonContextProps => {
    const { notifications, isExpanded } = this.props
    const { regular, fresh, pinned } = getNotificationsByShowMode(notifications)

    const hasRegular = regular.length > 0
    const hasFresh = fresh.length > 0

    return {
      isExpanded: notifications.length ? isExpanded || (hasFresh && !hasRegular) : false,
      counter: notifications.length - pinned.length,
      onClick: this.handleExpandButtonClick,
    }
  }

  private updateScrollableNodeMaxHeightThrottled = throttle(this.updateScrollableNodeMaxHeight, 50)

  private handleOuterKeyDown = ({ key }: KeyboardEvent) => {
    key === 'Escape' && this.close()
  }

  private handleRootNodeRef = (node: HTMLElement | null) => {
    this.rootNode = node
  }

  private handleScrollableComponentRef = (component: Scrollable | null) => {
    this.scrollableComponent = component
  }

  private handleScrollableNodeRef = (node: HTMLElement | null) => {
    this.scrollableNode = node
  }

  private handleRegularSectionNodeRef = (node: HTMLElement | null) => {
    this.regularSectionNode = node
  }

  private handlePinnedSectionNodeRef = (node: HTMLElement | null) => {
    this.pinnedSectionNode = node
  }

  private handleExpandButtonClick = () => {
    const { notifications } = this.props
    const { regular, fresh } = getNotificationsByShowMode(notifications)
    if (fresh.length && !regular.length) {
      this.autoClosingStep = 'trayWasExpanded'
      this.open()
    } else if (this.props.isExpanded || (!fresh.length && !regular.length)) {
      this.close()
    } else {
      this.open()
    }
  }

  private handleExpandAnimationStart = (_node: HTMLElement | null, done: AnimationCallback) => {
    const values = this.calculateTrayAnimationValues()
    this.handleTrayAnimationStart(done, values[0], values[1])
    this.props.isExpanded && this.props.notificationsShown()
  }

  private handleCollapseAnimationStart = (_node: HTMLElement | null, done: AnimationCallback) => {
    const values = this.calculateTrayAnimationValues()
    this.handleTrayAnimationStart(done, values[1], values[0])
  }

  private handleTrayAnimationStart (done: AnimationCallback, startValue: number, endValue: number) {
    const { autoClosingStep } = this
    if (autoClosingStep !== 'waitingForUserAction') {
      if (autoClosingStep === 'trayWasExpanded') {
        this.autoClosingStep = 'trayWasAnimated'
      }
      done()
      return
    }

    const { rootNode } = this
    if (!rootNode) {
      done()
      return
    }

    rootNode.classList.remove(styles.animated)
    rootNode.style.transform = `translateY(${ startValue }px)`

    cancelAnimationFrame(this.trayAnimationFrame)
    this.trayAnimationFrame = requestAnimationFrame(() => {
      this.scrollToBottom()
      rootNode.classList.add(styles.animated)
      rootNode.style.transform = `translateY(${ endValue }px)`
      this.trayAnimationCallbacks.add(done)
    })
  }

  private handleTrayTransitionEnd = ({ currentTarget, propertyName }: ReactTransitionEvent<HTMLElement>) => {
    if (propertyName !== 'transform') {
      return
    }

    currentTarget.classList.remove(styles.animated)
    currentTarget.removeAttribute('style')

    this.trayAnimationCallbacks.forEach((done) => done())
    this.trayAnimationCallbacks = new Set()
  }

  private handleItemEnterAnimationStart = (node: HTMLElement, done: AnimationCallback) => {
    node.classList.add(styles.enter) // it's required to calculate properly node.scrollHeight
    const startValue: ItemAnimationValue = { className: styles.enter, height: 0 }
    const endValue: ItemAnimationValue = { className: styles.enterActive, height: node.scrollHeight }
    this.handleItemAnimationStart(node, done, startValue, endValue)
  }

  private handleItemLeaveAnimationStart = (node: HTMLElement, done: AnimationCallback) => {
    const startValue: ItemAnimationValue = { className: styles.leave, height: node.getBoundingClientRect().height }
    const endValue: ItemAnimationValue = { className: styles.leaveActive, height: 0 }
    this.handleItemAnimationStart(node, done, startValue, endValue)
  }

  private handleItemAnimationStart (node: HTMLElement, done: AnimationCallback, startValue: ItemAnimationValue, endValue: ItemAnimationValue) {
    const notification = this.getNotificationByNode(node)
    if (!this.isItemAnimated(notification, true)) {
      node.classList.remove(startValue.className, endValue.className)
      return done()
    }

    node.classList.add(startValue.className)
    node.style.height = `${ startValue.height }px`
    const itemAnimationFrame = requestAnimationFrame(() => {
      this.itemAnimationFrames.delete(itemAnimationFrame)
      node.classList.add(endValue.className)
      node.style.height = `${ endValue.height }px`
      node.addEventListener('transitionend', ({ propertyName }: TransitionEvent) => {
        if (propertyName !== 'opacity') {
          return
        }
        node.classList.remove(startValue.className, endValue.className)
        done()
      })
    })
    this.itemAnimationFrames.add(itemAnimationFrame)
  }
}

export default withContext(OuterEventTrackerContext, 'outerEventTracker')(
  withContext(NotificationTrayContext, 'trayContext')(NotificationTray)
)
